// noinspection JSUnresolvedVariable,JSUnresolvedFunction

import Vue from 'vue'
import Vuex from 'vuex'
import api from '@/api'
import { DateTime } from 'luxon'
import router from "@/router"
import { ToastProgrammatic as Toast } from 'buefy'
import i18n from '@/i18n'
const paciReqTimeMinutes = 5
const paciBetweenReqTimeSeconds = 2.5

Vue.use(Vuex)

export default new Vuex.Store({
  state: {
    queryIdInfoResult: null,
    queryRequestStatusResult: null,
    updateIdInfoResult: null,
    paciRequestId: null,
    paciRequestDate: null,
    isMock: process.env.NODE_ENV !== 'production',
    navText: 'InformationNeed',
    preUpdateIdInfoResult: null,
    isPollingPaci: false,
    isPaciAuthenticated: false,

    embedTokenData: null,
    embedAuth: false,

    loading: true,

    //   ====== eezee airways =======
    eezeePlanInfoResult: null,
    userInfo: null,
    isRBPRAL: false,
    isFreeTrial: false,
  },
  actions: {
    async queryCustomerStatus({ state }, path) {
      try {
        // need to find a cleaner way to handle this
        if(path.startsWith('/eezee') || path.startsWith('/rbmz')) {
          const res = await api.getSimInfo()
          state.queryIdInfoResult = { msisdn: res.data.msisdn, status: res.data.status }
          state.isPaciAuthenticated = res.data.paciAuthenticated
          state.eezeePlanInfoResult = res.data.benefits
          state.isRBPRAL = res.data.isRBPRAL
          state.isFreeTrial = res.data.isFreeTrial
        } else {
          const res = await api.queryIdInfo()
          state.queryIdInfoResult = res.data
        }
      } catch (e) {
        if(path.startsWith('/eezee') || path.startsWith('/rbmz')) {
          state.eezeePlanInfoResult = 'error'
        }
        state.queryIdInfoResult = 'error'
        state.errorMessage = e.response.data.error
      }
    },
    async queryRequestStatus({ state }, requestId) {
      try {
        const res = await api.queryIdInfoRequest(requestId)
        state.queryRequestStatusResult = res.data
      } catch (e) {
        state.queryRequestStatusResult = 'error'
      }
    },
    async submitInfoUpdate({ state }, info) {
      try {
        const res = await api.updateIdInfo(info)
        if (res?.data?.isSuccess) {
          state.preUpdateIdInfoResult = res.data
        } else {
          state.updateIdInfoResult = res.data
        }
      } catch (e) {
        if (e?.response?.data?.error !== 'CivilIdMismatch')
          state.updateIdInfoResult = 'error'
      }
    },
    async setPaciRequestId({ state, dispatch }, requestId) {
      if (requestId === null) {
        localStorage.removeItem('request_id')
        localStorage.removeItem('request_date')

        state.paciRequestId = null
        state.paciRequestDate = null

        state.queryRequestStatusResult = null
        state.updateIdInfoResult = null

        return
      }

      const dtNow = Date.now().toString()

      state.paciRequestId = requestId
      state.paciRequestDate = dtNow

      localStorage.setItem('request_id', requestId)
      localStorage.setItem('request_date', dtNow)

      // fetch update after ~ seconds
      await new Promise((resolve) => setTimeout(resolve, 5000))
      await dispatch('queryRequestStatus', requestId)
    },
    async loadPaciRequestFromStorage({ state, dispatch }) {
      const reqId = localStorage.getItem('request_id') ?? null
      const reqDt = localStorage.getItem('request_date') ?? null

      if (reqId && reqDt) {
        const dt = DateTime.fromMillis(Number(reqDt))
        const minutesSinceReq = Math.abs(dt.diffNow('minutes').minutes)
        if (minutesSinceReq < paciReqTimeMinutes) {
          // only load the req from storage if it is with paci req time config
          state.paciRequestId = reqId
          state.paciRequestDate = dt.toJSDate()

          await dispatch('queryRequestStatus', reqId)

          // simulate adding a success response in updateIdInfoResult
          state.updateIdInfoResult = {
            isSuccess: true,
            result: {
              status: 'Success'
            }
          }
        }
      }
    },

    sendAnalyticsEvent(
      _,
      { eventAction, eventCategory, eventLabel, eventValue }
    ) {
      // eslint-disable-next-line no-undef
      if (gtag) {
        // eslint-disable-next-line no-undef
        gtag('event', eventAction, {
          event_category: eventCategory,
          event_label: eventLabel,
          event_value: eventValue
        })
      }
    },
    async submitPaciPartner({ state, dispatch }, {civilId, email}) {
      try {
        const res = await api.upsertPartnerPACI(civilId, email)
        if(res?.data?.isSuccess) {
          state.updateIdInfoResult = res.data
          await dispatch('setPaciRequestId', res.data.result.request_id)
          dispatch('reQueryPaci')
        }
      } catch (e) {
        if(e.response.data.error === 'must be over 18 years old') {
          Toast.open({
            duration: 6000,
            message: i18n.t('EezeeUnder18'),
            type: 'is-warning'
          })
        } else {
          Toast.open({
            duration: 6000,
            message: i18n.t('EezeePaciError'),
            type: 'is-danger'
          })
        }
        state.updateIdInfoResult = 'error'
      }
    },
    async scanEezee({ state }, scannedInfo) {
      state.userInfo = scannedInfo
    },
    async submitScanPartner({ state }, email) {
      try {
        const res = await api.upsertPartnerScan({ ...state.userInfo, email })
        return res.data.isSuccess
      } catch (e) {
        return false
      }
    },
    async queryPaciStatus({ dispatch, state }) {
      await dispatch('queryRequestStatus', state.paciRequestId)
      state.isPollingPaci = true
      const status = state.queryRequestStatusResult?.result?.status
      const paciRequestId = state.paciRequestId
      if (status && ['UserAccepted', 'UserDeclined'].includes(status)) {
        // clear storage and state
        localStorage.removeItem('request_id')
        localStorage.removeItem('request_date')
        state.isPollingPaci = false
        state.paciRequestId = null
        state.paciRequestDate = null
        state.queryRequestStatusResult = null
        state.updateIdInfoResult = null

        if(status === 'UserAccepted') {
          await api.postKycPartnerRecord({
            requestId: paciRequestId
          })
          await router.push({name: 'SuccessEezee'})
        }
      }
    },
    async pollPaciStatus({ dispatch, state }, { startTime, timeout, interval }) {
      const elapsedTime = Date.now() - startTime
      if (elapsedTime >= timeout || !state.paciRequestId) {
        return
      }

      await dispatch('queryPaciStatus')
      setTimeout(() => {
        dispatch('pollPaciStatus', { startTime, timeout, interval})
      }, interval)
    },
    async reQueryPaci({ dispatch, state }) {
      if(!state.paciRequestId) {
        return
      }
      const startTime = Date.now();
      const timeout = paciReqTimeMinutes * 60 * 1000 // Convert minutes to milliseconds
      const interval = paciBetweenReqTimeSeconds * 1000 // Convert seconds to milliseconds

      await dispatch('pollPaciStatus',{ startTime, timeout, interval})
    }
  },
  getters: {}
})
